import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { PurchaseLogLineItem } from "@interfaces/purchaseLogLineItem.interface";
import { StockStore } from "@interfaces/stockStore.interface";
import { StockStoreHistory } from "@interfaces/stockStoreHistory.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { StockStoreHistoryService } from "@services/stockStoreHistory.service";
import { StockStoreService } from "@services/stockStore.service";

interface BayGroup {
	bay: number;
	items: StockStore[];
	label: string;
}

interface RackGroup {
	bays: BayGroup[];
	location: string;
	maxBay: number;
	rack: string;
}

@Component({
	moduleId: module.id,
	selector: "storeMap",
	styleUrls: ["storeMap.css"],
	templateUrl: "storeMap.html"
})
export class StoreMapAdminComponent {
	private static readonly LOCATION_ORDER = ["Kings Norton", "Redditch Toolroom", "Redditch", "Other"];

	filterPopupVisible = false;
	headerPrimaryButtons: AdminHeaderButton[] = [
		{ icon: "fal fa-print", method: "printStockList", text: "Print" },
		{ icon: "fal fa-filter", method: "openFilterPopup", text: "Filter" },
		{ icon: "fal fa-sync", method: "refresh", text: "Refresh" }
	];
	isContractManagement = false;
	itemDetailLoading = false;
	itemDetailVisible = false;
	itemHistory: StockStoreHistory[] = [];
	private readonly lettersOnlyPattern = /^([A-Za-z]+)$/;
	loading = true;
	private readonly locationPattern = /^([A-Za-z]+)(\d+)/;
	maxBayCount = 0;
	popupCloseButtonOptions = { onClick: () => this.filterPopupVisible = false, text: "Close" };
	rackGroups: RackGroup[] = [];
	private rackValues = new Map<string, number>();
	selectedItem: StockStore | null = null;
	selectedRacks: string[] = [];
	private stockValues = new Map<number, number>();
	title = "Store Map";
	totalStockValue = 0;
	unknownItems: StockStore[] = [];
	unknownItemsValue = 0;

	constructor(private appSettingsService: AppSettingsService, private router: Router, private stockStoreHistoryService: StockStoreHistoryService, private stockStoreService: StockStoreService) {
		this.isContractManagement = CheckGroupPermissions(appSettingsService, "Contract Management");
		this.getInStock();
	}

	get allSelected(): boolean {
		return this.rackGroups.every(r => this.selectedRacks.includes(r.rack));
	}

	get filteredStockValue(): number {
		let total = 0;
		for (const rackGroup of this.filteredRackGroups) {
			for (const bayGroup of rackGroup.bays) {
				for (const item of bayGroup.items) {
					total += this.stockValues.get(item.id) ?? 0;
				}
			}
		}
		for (const item of this.unknownItems) {
			total += this.stockValues.get(item.id) ?? 0;
		}
		return total;
	}

	get filteredRackGroups(): RackGroup[] {
		if (this.selectedRacks.length === 0) return this.rackGroups;
		return this.rackGroups.filter(r => this.selectedRacks.includes(r.rack));
	}

	get locations(): string[] {
		const present = new Set(this.rackGroups.map(r => r.location));
		return StoreMapAdminComponent.LOCATION_ORDER.filter(l => present.has(l));
	}

	private calcItemValue(item: StockStore): number {
		const qty = Number(item.quantity) || 0;
		if (!qty) return 0;
		return this.resolveUnitCost(item) * qty;
	}

	closeItemDetail() {
		this.itemDetailVisible = false;
		this.selectedItem = null;
		this.itemHistory = [];
	}

	private computeStockValues(items: StockStore[]) {
		this.stockValues = new Map<number, number>();
		for (const item of items) {
			this.stockValues.set(item.id, this.calcItemValue(item));
		}

		// Pre-compute per-rack totals
		this.rackValues = new Map<string, number>();
		for (const rackGroup of this.rackGroups) {
			let rackTotal = 0;
			for (const bayGroup of rackGroup.bays) {
				for (const item of bayGroup.items) {
					rackTotal += this.stockValues.get(item.id) ?? 0;
				}
			}
			this.rackValues.set(rackGroup.rack, rackTotal);
		}

		// Pre-compute aggregate totals
		this.unknownItemsValue = this.unknownItems.reduce((sum, item) => sum + (this.stockValues.get(item.id) ?? 0), 0);
		this.totalStockValue = Array.from(this.stockValues.values()).reduce((sum, v) => sum + v, 0);
	}

	deselectAll() {
		this.selectedRacks = [];
	}

	editStoreItem(item: StockStore) {
		this.router.navigate([`admin//stores/${item.id}_edit`]);
	}

	formatSize(item: StockStore): string {
		const parts = [item.gauge, item.width, item.length].filter(v => v != null && v !== "");
		return parts.length ? parts.join(" × ") : "—";
	}

	getInStock() {
		this.loading = true;
		this.stockStoreService.getInStock()
			.subscribe(
				(res: any) => {
					this.processLocations(res.response);
					this.loading = false;
				},
				(err) => {
					console.log(err);
					this.loading = false;
				}
			);
	}

	getRackGroupsByLocation(location: string): RackGroup[] {
		return this.rackGroups.filter(r => r.location === location);
	}

	private getRackLocation(rack: string): string {
		const first = rack.charAt(0).toUpperCase();
		if ("ABCDEFGHIJ".includes(first)) return "Kings Norton";
		if ("LMN".includes(first)) return "Redditch Toolroom";
		if ("RS".includes(first)) return "Redditch";
		return "Other";
	}

	getRackValue(rackGroup: RackGroup): number {
		return this.rackValues.get(rackGroup.rack) ?? 0;
	}

	getRacksForLocation(location: string): string[] {
		return this.getRackGroupsByLocation(location).map(r => r.rack);
	}

	getStockValue(item: StockStore): number {
		return this.stockValues.get(item.id) ?? 0;
	}

	headerButtonClick(method: string) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	isLocationFullySelected(location: string): boolean {
		const racks = this.getRacksForLocation(location);
		return racks.length > 0 && racks.every(r => this.selectedRacks.includes(r));
	}

	isLowStock(item: StockStore): boolean {
		return Number(item.alertQuantity) > 0 && Number(item.quantity) <= Number(item.alertQuantity);
	}

	isOldStock(item: StockStore): boolean {
		const days = this.itemAgeDays(item);
		return days > 30 && days <= 60;
	}

	isRackSelected(rack: string): boolean {
		return this.selectedRacks.includes(rack);
	}

	isVeryOldStock(item: StockStore): boolean {
		return this.itemAgeDays(item) > 60;
	}

	itemAgeDays(item: StockStore): number {
		const created = item.createdAt ? new Date(item.createdAt).getTime() : null;
		if (!created) return 0;
		return Math.floor((Date.now() - created) / 86_400_000);
	}

	locationCssClass(location: string): string {
		const map: { [key: string]: string } = {
			"Kings Norton": "kings-norton",
			"Redditch": "redditch",
			"Redditch Toolroom": "redditch-toolroom"
		};
		return map[location] || "other";
	}

	openFilterPopup() {
		this.filterPopupVisible = true;
	}

	openItemDetail(item: StockStore) {
		this.selectedItem = item;
		this.itemDetailVisible = true;
		this.itemDetailLoading = true;
		this.itemHistory = [];
		this.stockStoreService.getDetails(item.id)
			.subscribe(
				(res: any) => {
					this.selectedItem = res.response;
					this.itemDetailLoading = false;
				},
				(err) => {
					console.log(err);
					this.itemDetailLoading = false;
				}
			);
		this.stockStoreHistoryService.getByItemId(item.id)
			.subscribe(
				(res: any) => {
					this.itemHistory = res.response;
				},
				(err) => {
					console.log(err);
				}
			);
	}

	printStockList() {
		const logoUrl = window.location.origin + "/assets/full-logo.jpg";
		const locationOrder = StoreMapAdminComponent.LOCATION_ORDER;
		const showValue = this.isContractManagement;

		const fmt = (v: number) => v.toLocaleString("en-GB", { currency: "GBP", maximumFractionDigits: 2, minimumFractionDigits: 2, style: "currency" });

		type PrintRow = { age: string; location: string; name: string; purchaseLog: string; qty: string; size: string; value: number };

		// Collect items grouped by location from the currently filtered racks
		const byLocation = new Map<string, PrintRow[]>();
		for (const rackGroup of this.filteredRackGroups) {
			if (!byLocation.has(rackGroup.location)) byLocation.set(rackGroup.location, []);
			for (const bayGroup of rackGroup.bays) {
				for (const item of bayGroup.items) {
					byLocation.get(rackGroup.location)!.push({
						age: `${this.itemAgeDays(item)} days`,
						location: item.location || "—",
						name: item.item?.name || "—",
						purchaseLog: item.purchaseLog ? `#${item.purchaseLog.id}` : "—",
						qty: String(item.quantity),
						size: this.formatSize(item),
						value: this.stockValues.get(item.id) ?? 0
					});
				}
			}
		}

		// Unlocated items
		if (this.unknownItems.length > 0) {
			byLocation.set("Unlocated", this.unknownItems.map(item => ({
				age: `${this.itemAgeDays(item)} days`,
				location: item.location || "UNKNOWN",
				name: item.item?.name || "—",
				purchaseLog: item.purchaseLog ? `#${item.purchaseLog.id}` : "—",
				qty: String(item.quantity),
				size: this.formatSize(item),
				value: this.stockValues.get(item.id) ?? 0
			})));
		}

		const orderedLocations = [...locationOrder.filter(l => byLocation.has(l)), ...(byLocation.has("Unlocated") ? ["Unlocated"] : [])];

		const valueColHeader = showValue ? "<th class=\"col-value\">Value</th>" : "";
		let grandTotal = 0;

		const sections = orderedLocations.map(loc => {
			const items = byLocation.get(loc)!;
			const sectionTotal = items.reduce((s, i) => s + i.value, 0);
			if (showValue) grandTotal += sectionTotal;

			const rows = items.map(i => `
				<tr>
					<td>${i.name}</td>
					<td>${i.size}</td>
					<td>${i.qty}</td>
					<td>${i.purchaseLog}</td>
					<td>${i.location}</td>
					<td>${i.age}</td>
					${showValue ? `<td class="col-value">${i.value > 0 ? fmt(i.value) : "—"}</td>` : ""}
				</tr>`).join("");

			const subtotalRow = showValue ? `
				<tr class="subtotal-row">
					<td colspan="6">Site Total — ${loc}</td>
					<td class="col-value">${fmt(sectionTotal)}</td>
				</tr>` : "";

			return `
			<h2>${loc} <span class="count">(${items.length} item${items.length !== 1 ? "s" : ""}${showValue ? " · " + fmt(sectionTotal) : ""})</span></h2>
			<table>
				<thead>
					<tr>
						<th>Material</th>
						<th>Size</th>
						<th>Quantity</th>
						<th>Purchase Log</th>
						<th>Location</th>
						<th>Age</th>
						${valueColHeader}
					</tr>
				</thead>
				<tbody>${rows}${subtotalRow}</tbody>
			</table>`;
		}).join("");

		const grandTotalBlock = showValue ? `<div class="grand-total">Total Stock Value: <strong>${fmt(grandTotal)}</strong></div>` : "";

		const html = `<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8" />
	<title>Stock List</title>
	<style>
		body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
		.header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; }
		.header img { height: 48px; }
		h1 { font-size: 1.25rem; margin: 0 0 4px 0; }
		h2 { font-size: 1rem; margin: 28px 0 8px 0; color: #333; border-bottom: 2px solid #111; padding-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em; }
		h2 .count { font-weight: normal; font-size: 0.85rem; color: #666; text-transform: none; letter-spacing: 0; }
		p.meta { font-size: 0.85rem; color: #555; margin: 0; }
		table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
		th { text-align: left; border-bottom: 2px solid #111; padding: 8px 12px; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; }
		td { padding: 8px 12px; border-bottom: 1px solid #ddd; font-size: 0.9rem; }
		.col-value { text-align: right; }
		.subtotal-row td { font-weight: 600; background: #f5f5f5; border-top: 2px solid #111; }
		.subtotal-row .col-value { font-weight: 700; }
		.grand-total { margin-top: 28px; padding: 12px 16px; background: #111; color: #fff; font-size: 1rem; text-align: right; border-radius: 4px; }
		* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
		@media print { body { padding: 0; } h2 { page-break-before: auto; } }
	</style>
</head>
<body>
	<div class="header">
		<div>
			<h1>Stock List</h1>
			<p class="meta">Printed: ${new Date().toLocaleString()}</p>
		</div>
		<img src="${logoUrl}" alt="BPL Logo" />
	</div>
	${sections || "<p>No items to display.</p>"}
	${grandTotalBlock}
</body>
</html>`;

		const win = window.open("", "_blank");
		if (win) {
			win.document.write(html);
			win.document.close();
			win.focus();
			win.print();
		}
	}

	private processLocations(items: StockStore[]) {
		const rackMap = new Map<string, Map<number, StockStore[]>>();
		this.unknownItems = [];

		for (const item of items) {
			const loc = (item.location || "").trim().toUpperCase();

			if (loc === "UNKNOWN" || loc === "") {
				this.unknownItems.push(item);
				continue;
			}

			const match = this.locationPattern.exec(loc);
			const lettersOnly = this.lettersOnlyPattern.exec(loc);

			let rack: string;
			let bay: number;

			if (match) {
				rack = match[1];
				bay = parseInt(match[2], 10);
			} else if (lettersOnly) {
				rack = lettersOnly[1];
				bay = 1;
			} else {
				this.unknownItems.push(item);
				continue;
			}

			if (!rackMap.has(rack)) {
				rackMap.set(rack, new Map<number, StockStore[]>());
			}
			const bayMap = rackMap.get(rack)!;
			if (!bayMap.has(bay)) {
				bayMap.set(bay, []);
			}
			bayMap.get(bay)!.push(item);
		}

		const sortedRacks = Array.from(rackMap.keys()).sort();

		this.rackGroups = sortedRacks.map(rack => {
			const bayMap = rackMap.get(rack)!;
			const sortedBays = Array.from(bayMap.keys()).sort((a, b) => a - b);
			const bays: BayGroup[] = sortedBays.map(bay => ({
				bay,
				items: bayMap.get(bay)!,
				label: bay === 1 ? "Floor (1)" : `Bay ${bay}`
			}));
			return { bays, location: this.getRackLocation(rack), maxBay: sortedBays[sortedBays.length - 1] ?? 1, rack };
		});

		this.maxBayCount = this.rackGroups.reduce((max, r) => Math.max(max, r.maxBay), 0);
		this.selectedRacks = sortedRacks.slice();
		this.computeStockValues(items);
	}

	refresh() {
		this.getInStock();
	}

	private resolveUnitCost(item: StockStore): number {
		const raw = item.purchaseLog?.lineItems;
		if (!raw) return 0;
		try {
			const lines: PurchaseLogLineItem[] = typeof raw === "string" ? JSON.parse(raw) : raw;
			if (!lines?.length) return 0;
			if (lines.length === 1) return lines[0].cost ?? 0;
			const nameLower = (item.item?.name || "").toLowerCase();
			const nameMatches = nameLower
				? lines.filter(l => l.lineDescription?.toLowerCase().includes(nameLower))
				: [];
			if (nameMatches.length === 1) return nameMatches[0].cost ?? 0;
			if (nameMatches.length > 1 && item.gauge != null && item.gauge !== "") {
				const gaugeNum = parseFloat(item.gauge);
				if (!isNaN(gaugeNum)) {
					const gaugeMatch = nameMatches.find(l => l.thickness === gaugeNum);
					if (gaugeMatch) return gaugeMatch.cost ?? 0;
				}
			}
			return (nameMatches[0] ?? lines[0]).cost ?? 0;
		} catch {
			return 0;
		}
	}

	selectAll() {
		this.selectedRacks = this.rackGroups.map(r => r.rack);
	}

	toggleLocation(location: string) {
		const racks = this.getRacksForLocation(location);
		if (this.isLocationFullySelected(location)) {
			this.selectedRacks = this.selectedRacks.filter(r => !racks.includes(r));
		} else {
			const toAdd = racks.filter(r => !this.selectedRacks.includes(r));
			this.selectedRacks = [...this.selectedRacks, ...toAdd];
		}
	}

	toggleRack(rack: string) {
		const idx = this.selectedRacks.indexOf(rack);
		if (idx > -1) {
			this.selectedRacks = this.selectedRacks.filter(r => r !== rack);
		} else {
			this.selectedRacks = [...this.selectedRacks, rack];
		}
	}

	viewPurchaseLog(item: StockStore) {
		this.router.navigate([`admin//purchaseLogs/${item.purchaseLog.lookupId}`]);
	}
}
